import { QuizQuestion } from '../quizEngine';

// Starknet & Cairo: 100 questions (30 easy, 40 average, 30 very hard).
export const starknetCairoQuestions: QuizQuestion[] = [
    // --- EASY (30) ---
    {
        id: 1,
        difficulty: 'easy',
        question: 'Starknet is:',
        choices: {
            a: 'a ZK rollup on Ethereum secured by STARK validity proofs',
            b: 'an optimistic rollup on Ethereum with a 7-day fraud window',
            c: 'a Bitcoin sidechain secured by merged proof-of-work mining',
            d: 'a standalone proof-of-stake L1 with its own validators',
        },
        answer: 'a',
        explanation: 'Starknet posts STARK validity proofs to Ethereum L1.',
    },
    {
        id: 2,
        difficulty: 'easy',
        question: 'Smart contracts on Starknet are written in:',
        choices: { a: 'Cairo', b: 'Solidity', c: 'Move', d: 'Rust (directly)' },
        answer: 'a',
        explanation: 'Cairo is the language for writing provable programs and Starknet contracts.',
    },
    {
        id: 3,
        difficulty: 'easy',
        question: 'A key property of STARK proofs is that they:',
        choices: {
            a: 'require no trusted setup',
            b: 'need a trusted ceremony, like Groth16',
            c: 'can only be verified off-chain',
            d: 'rely on pairing-friendly elliptic curves',
        },
        answer: 'a',
        explanation: 'STARKs use hash-based commitments and are transparent (no toxic waste).',
    },
    {
        id: 4,
        difficulty: 'easy',
        question: 'The fundamental data type in Cairo is the:',
        choices: {
            a: 'field element, `felt252`',
            b: '256-bit unsigned integer, `u256`',
            c: '64-bit float, `f64`',
            d: 'byte string, `ByteArray`',
        },
        answer: 'a',
        explanation: 'Everything reduces to field elements modulo a large prime; `u256` is built from two `u128`s.',
    },
    {
        id: 5,
        difficulty: 'easy',
        question: 'A `felt252` holds values modulo:',
        choices: {
            a: 'a prime P = 2^251 + 17*2^192 + 1',
            b: '2^252, the next power of two',
            c: '2^256, like the EVM word size',
            d: 'the secp256k1 group order n',
        },
        answer: 'a',
        explanation: 'Arithmetic wraps around the Stark prime, which is just above 2^251.',
    },
    {
        id: 6,
        difficulty: 'easy',
        question: 'The package/build tool for Cairo projects is:',
        choices: { a: 'Scarb', b: 'Cargo', c: 'Forge', d: 'npm' },
        answer: 'a',
        explanation: 'Scarb manages dependencies, builds and targets for Cairo.',
    },
    {
        id: 7,
        difficulty: 'easy',
        question: 'Which statement about accounts on Starknet is TRUE?',
        choices: {
            a: 'every account is a contract (native account abstraction)',
            b: 'accounts are plain keypairs, like Ethereum EOAs',
            c: 'only the sequencer can create new accounts for users',
            d: 'accounts hold token balances but can never contain code',
        },
        answer: 'a',
        explanation: 'There are no EOAs; signature schemes, multisig, session keys etc. are all just account-contract logic.',
    },
    {
        id: 8,
        difficulty: 'easy',
        question: 'Deploying new contract code on Starknet is a two-step process:',
        choices: {
            a: 'declare a class, then deploy an instance of it',
            b: 'compile to Sierra, then mine it into a block',
            c: 'stake STRK as a bond, then activate the contract',
            d: 'sign the bytecode, then broadcast it to Ethereum L1',
        },
        answer: 'a',
        explanation: 'A class is registered once network-wide; instances are deployed from it.',
    },
    {
        id: 9,
        difficulty: 'easy',
        question: "A contract's compiled code is identified by its:",
        choices: { a: 'class hash', b: 'nonce', c: 'storage root', d: 'contract address' },
        answer: 'a',
        explanation: "The class hash is a hash over the contract's compiled code, entry points and ABI; many addresses can share it.",
    },
    {
        id: 10,
        difficulty: 'easy',
        question: 'Which attribute marks a module as a Starknet contract?',
        choices: {
            a: '#[starknet::contract]',
            b: '#[starknet::component]',
            c: '#[starknet::interface]',
            d: '#[contract]',
        },
        answer: 'a',
        explanation: 'It generates the dispatch, storage access and ABI plumbing. `component` and `interface` are for reusable modules and traits.',
    },
    {
        id: 11,
        difficulty: 'easy',
        question: 'Contract storage is declared in a struct annotated with:',
        choices: { a: '#[storage]', b: '#[state]', c: '#[account]', d: '#[persistent]' },
        answer: 'a',
        explanation: 'Fields of the `#[storage]` struct become individually addressed storage variables.',
    },
    {
        id: 12,
        difficulty: 'easy',
        question: "To read the caller's address inside a Cairo contract you call:",
        choices: {
            a: 'get_caller_address()',
            b: 'get_tx_info().account',
            c: 'get_contract_address()',
            d: 'starknet::msg_sender()',
        },
        answer: 'a',
        explanation: "It comes from the Starknet syscall context. The tx info's account is the transaction originator, and `get_contract_address()` is your own address.",
    },
    {
        id: 13,
        difficulty: 'easy',
        question: "To read the contract's own address you call:",
        choices: { a: 'get_contract_address()', b: 'get_caller_address()', c: 'get_self_address()', d: 'address(this)' },
        answer: 'a',
        explanation: 'Also available via `get_execution_info`.',
    },
    {
        id: 14,
        difficulty: 'easy',
        question: 'Events in a Cairo contract are:',
        choices: {
            a: 'an enum marked #[event], emitted via `self.emit(...)`',
            b: 'return values of view functions, cached by nodes',
            c: 'entries written to a special events storage map',
            d: 'messages sent directly to an L1 log contract',
        },
        answer: 'a',
        explanation: 'Like EVM logs, events are for off-chain indexers, not on-chain reads.',
    },
    {
        id: 15,
        difficulty: 'easy',
        question: 'On Starknet, the node that orders transactions and produces blocks is the:',
        choices: { a: 'sequencer', b: 'prover', c: 'verifier', d: 'relayer' },
        answer: 'a',
        explanation: 'Sequencing has been operated by StarkWare, with decentralization on the roadmap.',
    },
    {
        id: 16,
        difficulty: 'easy',
        question: 'The component that produces the STARK proof of execution is the:',
        choices: { a: 'prover', b: 'sequencer', c: 'full node', d: 'bridge' },
        answer: 'a',
        explanation: 'The proof is then verified by a contract on Ethereum L1.',
    },
    {
        id: 17,
        difficulty: 'easy',
        question: 'Current (v3) Starknet transactions pay fees in:',
        choices: { a: 'STRK', b: 'BTC', c: 'USDC', d: 'SOL' },
        answer: 'a',
        explanation: 'v3 transactions pay in STRK; older v1 transactions paid in ETH.',
    },
    {
        id: 18,
        difficulty: 'easy',
        question: 'The `#[constructor]` attribute marks:',
        choices: {
            a: 'the function run once when the contract is deployed',
            b: 'a function that anyone may call at any time',
            c: 'the fallback that runs when no selector matches',
            d: 'the account function that validates signatures',
        },
        answer: 'a',
        explanation: 'It initializes storage; it does NOT run again on class replacement/upgrade.',
    },
    {
        id: 19,
        difficulty: 'easy',
        question: 'Cairo 1 syntax is most heavily inspired by:',
        choices: { a: 'Rust', b: 'Python', c: 'Go', d: 'Haskell' },
        answer: 'a',
        explanation: 'Traits, `impl` blocks, ownership/`Drop`, `Option`/`Result`, pattern matching, etc.',
    },
    {
        id: 20,
        difficulty: 'easy',
        question: 'A common testing framework for Starknet contracts is:',
        choices: {
            a: 'Starknet Foundry (snforge)',
            b: 'Foundry (forge test)',
            c: 'Anchor (anchor test)',
            d: 'Hardhat (npx hardhat test)',
        },
        answer: 'a',
        explanation: 'snforge runs Cairo tests with cheatcodes; `sncast` handles interactions. Plain Foundry and Hardhat target the EVM; Anchor targets Solana.',
    },
    {
        id: 21,
        difficulty: 'easy',
        question: "To expose an implementation's functions in a contract's ABI you typically use:",
        choices: {
            a: '#[abi(embed_v0)] on an impl of the interface trait',
            b: '#[public] on each function in the module',
            c: 'nothing; every function is public by default',
            d: "#[expose(v0)] on the contract's storage struct",
        },
        answer: 'a',
        explanation: '`#[external(v0)]` on individual functions is the other common form.',
    },
    {
        id: 22,
        difficulty: 'easy',
        question: 'A `#[starknet::interface]` trait defines:',
        choices: {
            a: 'the external interface, and generates a dispatcher for it',
            b: 'the storage layout that the contract must follow',
            c: 'the event schema that indexers use to decode logs',
            d: 'the constructor arguments and their default values for deploys',
        },
        answer: 'a',
        explanation: 'Callers use the generated `IFooDispatcher` to make typed cross-contract calls.',
    },
    {
        id: 23,
        difficulty: 'easy',
        question: '`ContractAddress` in Cairo is:',
        choices: {
            a: 'a dedicated type for contract and account addresses',
            b: 'an alias for `u256`, like `uint256` in Solidity',
            c: 'a `ByteArray` holding the hex string of the address',
            d: 'a 20-byte array, the same as an Ethereum address',
        },
        answer: 'a',
        explanation: 'Under the hood it is a bounded field element.',
    },
    {
        id: 24,
        difficulty: 'easy',
        question: 'Sized integer types like `u256` in Cairo, on overflow:',
        choices: {
            a: 'panic, because they carry overflow checks',
            b: 'wrap silently modulo 2^n, like raw felts',
            c: "saturate at the type's maximum value",
            d: 'return an `Option` that is `None` on overflow',
        },
        answer: 'a',
        explanation: 'Only raw `felt252` arithmetic wraps (mod the prime) with no protection.',
    },
    {
        id: 25,
        difficulty: 'easy',
        question: 'Starknet posts its proofs and state data to:',
        choices: {
            a: 'Ethereum L1 (the Starknet core contract)',
            b: 'IPFS, pinned by the sequencer',
            c: 'a separate data availability committee only',
            d: 'Bitcoin, via OP_RETURN outputs',
        },
        answer: 'a',
        explanation: 'L1 verification of the STARK proof is what gives Starknet its security.',
    },
    {
        id: 26,
        difficulty: 'easy',
        question: 'The syscall to send a message from L2 to L1 is:',
        choices: {
            a: 'send_message_to_l1_syscall',
            b: 'emit_l1_event_syscall',
            c: 'call_contract_syscall',
            d: 'l1_handler_syscall',
        },
        answer: 'a',
        explanation: 'The message becomes consumable by an L1 contract once the state update is proven.',
    },
    {
        id: 27,
        difficulty: 'easy',
        question: 'A function invoked by a message from an L1 contract is annotated:',
        choices: { a: '#[l1_handler]', b: '#[external(v0)]', c: '#[l1_bridge]', d: '#[constructor]' },
        answer: 'a',
        explanation: 'Its first parameter is the L1 sender address, which the handler must check.',
    },
    {
        id: 28,
        difficulty: 'easy',
        question: 'Cairo programs are special because they are:',
        choices: {
            a: 'provable: running one yields a proof it ran correctly',
            b: 'compiled straight to EVM bytecode for Ethereum',
            c: 'interpreted line by line by every Ethereum node',
            d: 'automatically parallelized across sequencer cores',
        },
        answer: 'a',
        explanation: 'Cairo = CPU Algebraic Intermediate Representation; execution produces a trace that can be STARK-proven.',
    },
    {
        id: 29,
        difficulty: 'easy',
        question: 'Which hash functions are native and cheap in Cairo / Starknet?',
        choices: { a: 'Pedersen and Poseidon', b: 'Keccak-256 and SHA-256', c: 'SHA-3 and RIPEMD-160', d: 'Blake3 and Argon2' },
        answer: 'a',
        explanation: 'Both are STARK-friendly; Poseidon is the newer one now used for many purposes. Keccak is available but expensive.',
    },
    {
        id: 30,
        difficulty: 'easy',
        question: 'A "contract class" versus a "contract instance":',
        choices: {
            a: 'the class is the code; an instance has an address and storage',
            b: 'they are the same thing under two different names',
            c: 'the class is per-user; the instance is shared globally',
            d: 'the instance holds the code; the class holds the storage',
        },
        answer: 'a',
        explanation: 'Many instances can share one class hash.',
    },

    // --- AVERAGE (40) ---
    {
        id: 31,
        difficulty: 'medium',
        question: 'Storage variable addresses in a Starknet contract are:',
        choices: {
            a: 'a hash of the variable name (plus map keys), not a slot index',
            b: 'sequential slots 0, 1, 2, ... in declaration order, like the EVM',
            c: 'offsets the developer assigns by hand in the storage struct',
            d: 'a fresh pseudo-random address chosen on each first write',
        },
        answer: 'a',
        explanation: 'The base address is `sn_keccak(name)`, with map keys hashed in. This is why upgrades must preserve variable names/types.',
    },
    {
        id: 32,
        difficulty: 'medium',
        question: 'A `Map` (formerly `LegacyMap`) storage field is accessed with:',
        choices: {
            a: '`.read(key)` / `.write(key, value)` (or `.entry(key)`)',
            b: 'array indexing, like `self.map[key] = value`',
            c: '`.get(key)` / `.insert(key, value)`, like a HashMap',
            d: '`.load(key)` / `.store(key, value)` via the Store trait',
        },
        answer: 'a',
        explanation: 'Nested maps chain the key hashing.',
    },
    {
        id: 33,
        difficulty: 'medium',
        question: 'The receiver `self: @ContractState` versus `ref self: ContractState`:',
        choices: {
            a: '`@` is a read-only snapshot; `ref` lets it modify storage',
            b: 'they are interchangeable; the compiler chooses one for you',
            c: '`@` allows storage writes, while `ref` is read-only',
            d: '`ref` is only allowed in constructors and upgrades',
        },
        answer: 'a',
        explanation: 'The compiler enforces that `@self` functions cannot write storage.',
    },
    {
        id: 34,
        difficulty: 'medium',
        question: "`library_call_syscall` is analogous to the EVM's:",
        choices: {
            a: "`delegatecall`: another class's code in this contract's context",
            b: 'a plain external `call` into another deployed contract',
            c: '`staticcall`: a read-only call into another class hash',
            d: '`create2`: deploying a class instance at a deterministic address',
        },
        answer: 'a',
        explanation: "It executes a class hash's logic against the caller's state.",
    },
    {
        id: 35,
        difficulty: 'medium',
        question: "`call_contract_syscall` is analogous to the EVM's:",
        choices: {
            a: "a regular external `call` (the callee's own storage)",
            b: "`delegatecall`, running code in the caller's storage",
            c: '`staticcall`, forbidding any storage writes in the callee',
            d: 'an internal function call within the same contract',
        },
        answer: 'a',
        explanation: 'Dispatchers generated from `#[starknet::interface]` wrap this syscall.',
    },
    {
        id: 36,
        difficulty: 'medium',
        question: 'To upgrade a deployed contract in place, Starknet provides:',
        choices: {
            a: '`replace_class_syscall`, swapping the class the address runs',
            b: 'a separate proxy contract, since there is no native mechanism',
            c: '`selfdestruct` plus a redeploy at the same address',
            d: 'writing the new class hash directly into storage slot 0',
        },
        answer: 'a',
        explanation: 'No separate proxy is needed; the address keeps its storage and just runs new code.',
    },
    {
        id: 37,
        difficulty: 'medium',
        question: 'When you `replace_class_syscall` to a new class, the new class must:',
        choices: {
            a: 'keep a compatible storage layout (same names and types)',
            b: 'have exactly the same class hash as the class it replaces',
            c: 'be smaller in bytecode than the class it replaces',
            d: 'be declared by the same account that originally deployed it',
        },
        answer: 'a',
        explanation: 'Storage addresses come from variable names, so renaming/removing/retyping them breaks reads of existing state.',
    },
    {
        id: 38,
        difficulty: 'medium',
        question: 'Native account abstraction means a transaction from an account first invokes:',
        choices: {
            a: '`__validate__` (checks signature/nonce), then `__execute__`',
            b: '`__execute__` directly, which checks its own signature',
            c: "the sequencer's built-in ECDSA signature check, then the call",
            d: 'an L1 precompile that verifies the signature first',
        },
        answer: 'a',
        explanation: "Separating validation from execution mirrors ERC-4337's design, but built into the protocol.",
    },
    {
        id: 39,
        difficulty: 'medium',
        question: "If an account's `__validate__` panics or returns an invalid result:",
        choices: {
            a: 'the tx is rejected, and `__execute__` never runs',
            b: '`__execute__` runs anyway, but its state changes revert',
            c: 'the sequencer pays the fee itself and still includes the tx',
            d: 'the tx is kept in the mempool and retried in the next block',
        },
        answer: 'a',
        explanation: 'The transaction is not included. This is why `__validate__` has restrictions: a cheap, safe pre-check protects the sequencer.',
    },
    {
        id: 40,
        difficulty: 'medium',
        question: 'A `deploy_account` transaction is special because:',
        choices: {
            a: 'it deploys the account that validates and pays for it',
            b: "it needs no signature, since the account doesn't exist yet",
            c: 'it can only be submitted by the sequencer itself',
            d: 'it deploys the account on L1 and mirrors it to L2',
        },
        answer: 'a',
        explanation: 'The address is computed deterministically, so it can be pre-funded at its counterfactual address before deployment.',
    },
    {
        id: 41,
        difficulty: 'medium',
        question: '`deploy_syscall` is used to:',
        choices: {
            a: 'deploy an instance of an already-declared class',
            b: 'declare a brand-new class from inside a contract',
            c: 'upgrade the calling contract to a new class',
            d: 'send a deployment request to an L1 contract',
        },
        answer: 'a',
        explanation: 'It returns the new contract address and constructor return data. Declaring is a separate transaction type.',
    },
    {
        id: 42,
        difficulty: 'medium',
        question: 'The Universal Deployer Contract (UDC) exists to:',
        choices: {
            a: 'let any caller deploy contracts through a standard interface',
            b: 'store every class hash that has ever been declared',
            c: 'verify STARK proofs on behalf of other contracts',
            d: 'bridge ERC-20 tokens from L1 to newly deployed L2 contracts',
        },
        answer: 'a',
        explanation: 'It wraps `deploy_syscall` so wallets/dapps can deploy without their own factory.',
    },
    {
        id: 43,
        difficulty: 'medium',
        question: 'Function selectors on Starknet are:',
        choices: {
            a: '`sn_keccak(name)`: Keccak-256 masked to 250 bits',
            b: 'the first 4 bytes of keccak256 of the signature',
            c: '`poseidon(name, param_types)` as a full felt',
            d: "the function's index in the ABI, starting at 0",
        },
        answer: 'a',
        explanation: 'A full field element, not a 4-byte truncation; `sn_keccak` masks Keccak-256 so it fits the field.',
    },
    {
        id: 44,
        difficulty: 'medium',
        question: "When a Starknet transaction's execution panics:",
        choices: {
            a: "its state changes revert, but it's still included and charged",
            b: 'the chain halts until the sequencer manually skips it',
            c: 'the call silently returns zero and execution continues',
            d: 'the entire block it landed in is rolled back and then rebuilt',
        },
        answer: 'a',
        explanation: 'Sierra made reverting executions provable, which earlier Cairo could not do.',
    },
    {
        id: 45,
        difficulty: 'medium',
        question: '`#[generate_trait]` is used to:',
        choices: {
            a: 'generate a trait for an `impl` block, e.g. internal helpers',
            b: "generate the contract's ABI from its storage struct",
            c: 'derive `Serde` and `Store` implementations for a struct',
            d: 'create a dispatcher for calling another contract',
        },
        answer: 'a',
        explanation: 'It saves writing the trait declaration by hand for `InternalImpl`-style blocks.',
    },
    {
        id: 46,
        difficulty: 'medium',
        question: 'The `Store` trait in Cairo defines:',
        choices: {
            a: 'how a type is laid out in and read from contract storage',
            b: 'a key-value database client for off-chain indexing',
            c: 'how a type is emitted and indexed as part of an event',
            d: 'how a type is (de)serialized into calldata felts',
        },
        answer: 'a',
        explanation: 'Primitive types implement it already; custom structs usually `#[derive(starknet::Store)]`. Calldata (de)serialization is `Serde`.',
    },
    {
        id: 47,
        difficulty: 'medium',
        question: '`#[derive(Drop, Serde)]` on a struct that crosses the ABI:',
        choices: {
            a: 'makes it droppable and serializable for calls and returns',
            b: 'makes it storable as a field of the storage struct',
            c: 'registers it as an event type that can be emitted',
            d: 'marks it as safe to keep across class upgrades unchanged',
        },
        answer: 'a',
        explanation: '`Serde` handles the felt-array (de)serialization at the call boundary; storage needs `Store`.',
    },
    {
        id: 48,
        difficulty: 'medium',
        question: 'Cairo has no `null`; optionality is expressed with:',
        choices: {
            a: '`Option<T>` (`Some` / `None`)',
            b: 'a zero `felt252` sentinel value',
            c: 'an empty `Array<T>` of length 0',
            d: 'a special nullable `Void` type',
        },
        answer: 'a',
        explanation: 'Errors use `Result<T, E>` / `panic`.',
    },
    {
        id: 49,
        difficulty: 'medium',
        question: 'A dispatcher such as `IERC20Dispatcher { contract_address }` is:',
        choices: {
            a: 'a generated struct for calling a contract via its interface',
            b: "a typed pointer to one variable in another contract's storage",
            c: 'an account-side signer that authorizes outgoing calls',
            d: 'an L1-to-L2 message queued for an `#[l1_handler]`',
        },
        answer: 'a',
        explanation: 'It wraps `call_contract_syscall`. `IERC20LibraryDispatcher { class_hash }` is the delegatecall-style variant.',
    },
    {
        id: 50,
        difficulty: 'medium',
        question: 'Why avoid raw `felt252` for token/financial arithmetic?',
        choices: {
            a: 'it wraps mod the prime with no overflow checks; use `u256`',
            b: 'it is several times slower than `u256` arithmetic',
            c: 'it cannot represent any value above 2^64 at all',
            d: 'the compiler forbids storing `felt252` values in contract storage',
        },
        answer: 'a',
        explanation: 'Silent modular wrap is a footgun for balances and supply math; `u256`/`u128` panic on overflow.',
    },
    {
        id: 51,
        difficulty: 'medium',
        question: '`get_execution_info()` exposes:',
        choices: {
            a: 'block info, tx info (sender, nonce, version), and addresses',
            b: 'a Merkle proof of the full contract storage tree',
            c: 'the STARK proof that is being built for the current block of txs',
            d: "the current sequencer's signing key and fee config",
        },
        answer: 'a',
        explanation: 'It is the one syscall that bundles the execution context, including caller and contract addresses.',
    },
    {
        id: 52,
        difficulty: 'medium',
        question: 'Events on Starknet are:',
        choices: {
            a: 'not readable on-chain; they are for off-chain indexers',
            b: 'readable later by contracts through a `get_event` syscall',
            c: 'stored in the `#[storage]` struct as a log array',
            d: 'automatically forwarded to L1 as L2-to-L1 messages',
        },
        answer: 'a',
        explanation: 'Like EVM logs. If a contract needs to read the data later, it must also store it.',
    },
    {
        id: 53,
        difficulty: 'medium',
        question: 'Cairo "components" (the OpenZeppelin-style modules) are:',
        choices: {
            a: 'reusable storage + logic + events embedded by composition',
            b: 'external libraries that are only reachable by library calls',
            c: 'parent contracts that a contract inherits storage from',
            d: 'off-chain plugins that extend what the sequencer does',
        },
        answer: 'a',
        explanation: "A contract adds a component's storage via `#[substorage(v0)]` and its impl via `component!`: composition, not inheritance.",
    },
    {
        id: 54,
        difficulty: 'medium',
        question: 'With `#[substorage(v0)]`, how do embedded components avoid storage collisions with the host contract?',
        choices: {
            a: 'by using unique, prefixed variable names (e.g. `Ownable_owner`)',
            b: "the macro hashes each component's variables under its own sub-path",
            c: 'the compiler renumbers storage slots at deploy time',
            d: 'components are not allowed to declare storage of their own',
        },
        answer: 'a',
        explanation: "`v0` substorage is flat: component variables live at `sn_keccak(name)` just like the host's, so identically named variables would collide. OpenZeppelin prefixes names to keep them unique.",
    },
    {
        id: 55,
        difficulty: 'medium',
        question: 'L1-to-L2 messages are delivered by:',
        choices: {
            a: 'paying on L1; the sequencer then calls the L2 `#[l1_handler]`',
            b: 'the L2 contract polling the L1 core contract once every block',
            c: 'an off-chain relayer network, with no record kept on L1',
            d: 'a light client of Ethereum running inside Starknet',
        },
        answer: 'a',
        explanation: 'The message is registered on the L1 core contract and consumed by the L2 handler.',
    },
    {
        id: 56,
        difficulty: 'medium',
        question: 'L2-to-L1 messages must be:',
        choices: {
            a: 'consumed on L1 once the state update carrying it is proven',
            b: 'readable instantly by L1 contracts, with no proof required',
            c: 'signed by every Starknet validator before delivery',
            d: 'delivered within the same L1 block they were sent in',
        },
        answer: 'a',
        explanation: 'An L1 contract calls `consumeMessageFromL2` after the proof lands; finality on L1 gates it.',
    },
    {
        id: 57,
        difficulty: 'medium',
        question: "Cairo's execution memory model is:",
        choices: {
            a: "write-once: a memory cell can't change once assigned",
            b: 'freely mutable, like registers and RAM on a normal CPU',
            c: 'stack-only, with no addressable memory cells at all',
            d: 'garbage-collected, with cells freed between calls',
        },
        answer: 'a',
        explanation: 'This is what makes execution efficiently provable; persistent storage is a separate mutable key-value space.',
    },
    {
        id: 58,
        difficulty: 'medium',
        question: 'The class hash is a hash over:',
        choices: {
            a: 'the compiled code (Sierra/CASM), entry points and ABI',
            b: "the deployer's address, a salt and the constructor calldata",
            c: "the contract's current storage root and nonce",
            d: 'the source files, so identical code differs per author',
        },
        answer: 'a',
        explanation: 'Two byte-identical contracts share a class hash and only need declaring once network-wide. Deployer/salt/calldata feed the address, not the class hash.',
    },
    {
        id: 59,
        difficulty: 'medium',
        question: 'A Starknet contract address is derived deterministically from:',
        choices: {
            a: 'deployer, salt, class hash and constructor calldata',
            b: 'a random number that the sequencer assigns at deploy time',
            c: "the deployer's nonce only, like Ethereum's CREATE",
            d: "the block number and the transaction's index in it",
        },
        answer: 'a',
        explanation: 'Plus a constant prefix, with the calldata hashed. This makes counterfactual (pre-funded) deployment possible.',
    },
    {
        id: 60,
        difficulty: 'medium',
        question: 'Multicall on Starknet is native because:',
        choices: {
            a: '`__execute__` takes an array of `Call`s under one signature',
            b: 'a system Multicall contract that every wallet must route through',
            c: "the sequencer batches unrelated users' transactions together",
            d: "it isn't: each call needs its own signed transaction",
        },
        answer: 'a',
        explanation: 'One signed transaction can do approve+swap+deposit in sequence, atomically.',
    },
    {
        id: 61,
        difficulty: 'medium',
        question: 'Division of two `felt252` values in Cairo is:',
        choices: {
            a: 'multiplication by the modular inverse of the divisor',
            b: 'integer floor division, rounding the result toward zero',
            c: 'a compile error, since felts do not support division at all',
            d: 'the same as `u256` division, then reduced mod P',
        },
        answer: 'a',
        explanation: 'Field division: `6 / 2` gives 3, but `1 / 2` gives a large field element, not 0.',
    },
    {
        id: 62,
        difficulty: 'medium',
        question: 'Sierra ("Safe Intermediate Representation") exists mainly for:',
        choices: {
            a: 'guaranteeing every tx can be proven, so even reverts pay fees',
            b: 'shrinking contract bytecode to cut the L1 data-availability cost',
            c: "adding garbage collection to Cairo's memory model",
            d: 'encrypting contract code so only the sequencer sees it',
        },
        answer: 'a',
        explanation: 'Every Sierra program has a valid lowering to CASM that runs to completion. Before Sierra, an unsatisfiable trace could not be proven, enabling a DoS on the fee mechanism.',
    },
    {
        id: 63,
        difficulty: 'medium',
        question: 'Getting a truly random number inside a Starknet contract:',
        choices: {
            a: 'requires an oracle or VRF; block values are unsafe',
            b: 'is done with the built-in `get_random_felt()` syscall',
            c: 'uses the nonce of the STARK proof for the current block',
            d: 'is impossible, so games must run fully off-chain',
        },
        answer: 'a',
        explanation: 'Block values are sequencer-influenced, the same class of problem as `block.timestamp` randomness on Ethereum.',
    },
    {
        id: 64,
        difficulty: 'medium',
        question: 'A `LibraryDispatcher` differs from a normal `Dispatcher` in that it:',
        choices: {
            a: "library-calls a class hash, running it in the caller's context",
            b: 'calls a deployed contract address that keeps its own storage',
            c: 'can only call view functions, never state-changing ones',
            d: 'queues the call asynchronously for the next block',
        },
        answer: 'a',
        explanation: 'It uses `library_call_syscall` (delegatecall-style). Only ever library-call class hashes you fully trust.',
    },
    {
        id: 65,
        difficulty: 'medium',
        question: 'Starknet meters execution using:',
        choices: {
            a: 'Cairo steps and builtins, plus L1 data for state diffs',
            b: 'wall-clock milliseconds measured by the sequencer',
            c: 'the number of storage variables the contract declares',
            d: 'a flat fee per transaction, regardless of the work done',
        },
        answer: 'a',
        explanation: 'Not EVM-style per-opcode gas; v3 transactions express this via `l1_gas` / `l2_gas` resource bounds.',
    },
    {
        id: 66,
        difficulty: 'medium',
        question: 'Why must an `#[l1_handler]` explicitly check its `from_address` argument?',
        choices: {
            a: 'any L1 address can message it, so it must allowlist the sender',
            b: 'checking it early saves gas by skipping payload deserialization',
            c: 'the compiler rejects handlers that ignore that argument',
            d: "it is needed to derive the handler's storage addresses",
        },
        answer: 'a',
        explanation: 'Like checking `msg.sender` on a bridge: without the check, anyone on L1 could trigger privileged L2 logic.',
    },
    {
        id: 67,
        difficulty: 'medium',
        question: '`replace_class_syscall` does NOT:',
        choices: {
            a: "run the new class's constructor",
            b: 'change which code the address runs',
            c: "keep the contract's address and storage",
            d: 'require the new class to be declared first',
        },
        answer: 'a',
        explanation: 'Constructors run only on deploy, so upgrade init logic must be a separate, access-controlled function.',
    },
    {
        id: 68,
        difficulty: 'medium',
        question: 'Reentrancy on Starknet is:',
        choices: {
            a: 'possible (A -> B -> A) and not prevented; you need a guard',
            b: 'impossible, because execution memory is write-once',
            c: "blocked by the account contract's `__validate__` step",
            d: 'only a risk inside `#[l1_handler]` entry points',
        },
        answer: 'a',
        explanation: 'Cross-contract callback patterns need an explicit reentrancy guard, as on Ethereum.',
    },
    {
        id: 69,
        difficulty: 'medium',
        question: 'Why is using `<` on `felt252` values a common footgun?',
        choices: {
            a: 'values wrap mod P, so "negative" results compare as huge numbers',
            b: 'felt comparison is non-deterministic across different provers',
            c: 'it silently compares only the low 128 bits of each value',
            d: 'it panics at runtime whenever either value exceeds 2^128',
        },
        answer: 'a',
        explanation: '`0 - 1` is P - 1, the largest felt, so an underflowed value sorts as enormous. Use sized integers (`u128`/`u256`) when order matters.',
    },
    {
        id: 70,
        difficulty: 'medium',
        question: 'Cairo\'s "hint" / nondeterministic computation lets the prover:',
        choices: {
            a: 'supply a witness value that the program then cheaply verifies',
            b: 'skip the parts of the program that are expensive to prove',
            c: 'choose the order of transactions within the block',
            d: "read another contract's storage for free, without a syscall",
        },
        answer: 'a',
        explanation: "E.g. a claimed inverse or square root that the program checks with constraints. Guess-then-verify is central to Cairo's efficiency.",
    },

    // --- VERY HARD (30) ---
    {
        id: 71,
        difficulty: 'hard',
        question: 'Why must a Starknet contract not assume `get_caller_address()` is non-zero?',
        choices: {
            a: 'on some entry paths, like an `#[l1_handler]`, the caller is 0',
            b: 'the sequencer zeroes it for privacy on every transaction',
            c: 'it reads as zero inside any `@self` view function call',
            d: 'it is zero whenever the call comes in through an account multicall',
        },
        answer: 'a',
        explanation: "Outermost entry points (an L1 handler, or an account's own `__execute__`) have no calling contract. Access-control checks must handle the zero-caller case explicitly.",
    },
    {
        id: 72,
        difficulty: 'hard',
        question: "The account `__validate__` function is restricted (no arbitrary external calls, limited access to other contracts' state, gas-bounded) because:",
        choices: {
            a: 'the sequencer must judge validity cheaply and without DoS risk',
            b: '`__validate__` executes on Ethereum L1, where calls cost too much',
            c: 'it is compiled to a restricted Cairo subset without syscalls',
            d: 'the prover cannot prove external calls made during validation',
        },
        answer: 'a',
        explanation: "A transaction whose validity flips due to unrelated state would let attackers grief the sequencer. The restrictions mirror ERC-4337's validation rules.",
    },
    {
        id: 73,
        difficulty: 'hard',
        question: 'Before Sierra, why could a reverting transaction NOT simply be included and charged?',
        choices: {
            a: "a failing trace couldn't be proven, so reverts couldn't be charged",
            b: 'reverts were banned by governance as a security risk to the L1 bridges',
            c: "the fee token didn't exist, so there was nothing to charge",
            d: 'Cairo had no `panic`, so calls could never actually fail',
        },
        answer: 'a',
        explanation: "An unsatisfiable execution couldn't be part of a valid block, enabling a DoS where cheap-to-trigger failures cost the sequencer. Sierra guarantees a provable lowering exists, so panics become a normal, provable path.",
    },
    {
        id: 74,
        difficulty: 'hard',
        question: 'For a storage `Map`, the slot address is computed roughly as:',
        choices: {
            a: 'h(sn_keccak(name), k1), then h(..., k2) for nested keys',
            b: '`sn_keccak(name) + k1 + k2`, added as field elements',
            c: 'a random address chosen the first time it is written',
            d: '`keccak256(k1 || k2)` truncated to 20 bytes, like the EVM',
        },
        answer: 'a',
        explanation: 'The base address is the masked `sn_keccak` of the variable name, and each key is chained in with a Pedersen hash. Distinct maps only collide if their variable names collide.',
    },
    {
        id: 75,
        difficulty: 'hard',
        question: '`sn_keccak` is:',
        choices: {
            a: 'Keccak-256 masked to 250 bits so it fits in a felt',
            b: 'a StarkWare hash unrelated to Keccak, built on Poseidon',
            c: 'Keccak-256 truncated to 4 bytes, like EVM selectors',
            d: 'the Pedersen hash of the full Keccak-256 output',
        },
        answer: 'a',
        explanation: 'Full Keccak-256 output can exceed the Stark prime, so it is masked. It is used for selectors and storage-name hashing.',
    },
    {
        id: 76,
        difficulty: 'hard',
        question: "Cairo's write-once execution memory can still back mutable-looking contract storage because:",
        choices: {
            a: 'storage is a separate Merkle-committed key-value space',
            b: 'storage is also write-once; values never really change',
            c: 'the sequencer wipes and rewrites memory at each block',
            d: 'storage lives on Ethereum L1, and Cairo only caches it',
        },
        answer: 'a',
        explanation: 'The write-once rule applies to the execution trace; each transaction reads the pre-state and produces a new committed post-state.',
    },
    {
        id: 77,
        difficulty: 'hard',
        question: '`library_call_syscall` storage semantics mean:',
        choices: {
            a: "the class runs on the caller's storage, so a bad hash can rewrite it",
            b: 'the class runs against its own separate storage, isolated from the caller',
            c: 'it cannot touch storage at all and can only return values',
            d: 'any storage write it attempts reverts the whole call tree',
        },
        answer: 'a',
        explanation: "The target class's code runs in the caller's storage and address context, the exact delegatecall trust problem from the EVM. Only library-call trusted classes.",
    },
    {
        id: 78,
        difficulty: 'hard',
        question: "The `replace_class_syscall` upgrade path's classic footgun is:",
        choices: {
            a: 'assuming the constructor runs, and storage-layout drift',
            b: 'the class hash change also changing the contract address',
            c: 'all past events of the contract being wiped when the class changes',
            d: 'the sequencer rejecting upgrades not signed by StarkWare',
        },
        answer: 'a',
        explanation: 'The new constructor does not run, and any storage-layout change silently corrupts reads. Upgrade init must be an explicit guarded function, and storage vars must be name/type-stable.',
    },
    {
        id: 79,
        difficulty: 'hard',
        question: 'A Starknet contract address is computed as (conceptually):',
        choices: {
            a: 'h(prefix, deployer, salt, class_hash, h(calldata)), masked',
            b: 'keccak256(rlp(deployer, nonce))[12:], like Ethereum',
            c: 'h(prefix, deployer, nonce, class_hash), masked to the field',
            d: 'h(prefix, salt, h(calldata)), without the class hash',
        },
        answer: 'a',
        explanation: 'With deployer = 0 for "deploy from zero" (UDC-style). There is no nonce, so anyone can precompute the address, enabling counterfactual funding.',
    },
    {
        id: 80,
        difficulty: 'hard',
        question: 'Fees on Starknet are charged even when `__execute__` reverts, as long as:',
        choices: {
            a: '`__validate__` passed; a failed validation costs the sequencer',
            b: 'the transaction is a `deploy_account` rather than an invoke',
            c: 'the caller opted in by setting a flag on the transaction',
            d: 'never; reverted transactions are always free on Starknet',
        },
        answer: 'a',
        explanation: 'Validation is the gatekeeper; once past it, the account is committed to paying, which is why validation is restricted.',
    },
    {
        id: 81,
        difficulty: 'hard',
        question: 'STARKs versus pairing-based SNARKs (e.g. Groth16) offer:',
        choices: {
            a: 'no trusted setup, hash-based security, but larger proofs',
            b: 'a per-circuit trusted ceremony, but much smaller proofs',
            c: 'proofs that are always smaller than SNARKs, with no downside',
            d: "no on-chain verification, since Ethereum can't check them",
        },
        answer: 'a',
        explanation: 'Transparency (no toxic waste) and plausibly post-quantum hash assumptions are the headline STARK advantages; proof size is the cost.',
    },
    {
        id: 82,
        difficulty: 'hard',
        question: 'Why did Starknet move selector/state hashing toward Poseidon?',
        choices: {
            a: "it's STARK-friendly and far cheaper to prove than Pedersen",
            b: 'it is more collision-resistant than SHA-3 and Keccak-256',
            c: 'Pedersen was broken by a practical collision attack',
            d: 'it outputs shorter 128-bit hashes, which saves storage space',
        },
        answer: 'a',
        explanation: 'Arithmetization-friendly hashes dominate proving cost, so they are chosen for performance, especially for multi-input hashing.',
    },
    {
        id: 83,
        difficulty: 'hard',
        question: 'v3 (resource-bounds) transactions introduced:',
        choices: {
            a: 'STRK fees with explicit `l1_gas` / `l2_gas` resource bounds',
            b: 'a fixed fee per transaction, regardless of resources used',
            c: 'removal of the fee market in favour of sequencer quotas',
            d: 'gas priced only in ETH, capped by a single `max_fee` field',
        },
        answer: 'a',
        explanation: 'Plus paymaster and account-deployment-data fields. It formalizes a multi-dimensional (L1 data + L2 compute) fee model; the single `max_fee` in ETH was the older v1 format.',
    },
    {
        id: 84,
        difficulty: 'hard',
        question: '`declare` cost versus deployment cost:',
        choices: {
            a: 'declaring is paid once per class; deploying is per instance',
            b: 'declaring is free, and only deploying an instance costs anything',
            c: 'both are charged again in full for every instance deployed',
            d: 'declaring gets pricier each time the same class is repeated',
        },
        answer: 'a',
        explanation: 'A unique class is declared once network-wide; deployment cost (storage + constructor) is per instance. This is why libraries publish one class that many contracts library-call.',
    },
    {
        id: 85,
        difficulty: 'hard',
        question: 'An L1-to-L2 message is protected against forgery from unauthorized L1 senders because:',
        choices: {
            a: 'L1 records the sender, and the handler receives it to check',
            b: 'only the sequencer itself is able to send L1-to-L2 messages',
            c: 'every message is co-signed by a StarkWare-held bridge key',
            d: 'the payload is encrypted so only the target contract can read it',
        },
        answer: 'a',
        explanation: 'The core contract registers `(from_address, to_address, selector, payload, nonce)`, and the `#[l1_handler]` gets `from_address` as its first argument; it must allowlist the legitimate L1 bridge.',
    },
    {
        id: 86,
        difficulty: 'hard',
        question: '`get_caller_address()` inside code reached via a `LibraryDispatcher` returns:',
        choices: {
            a: "the host's caller, since a library call adds no new frame",
            b: 'the class hash that is currently being library-called',
            c: 'the zero address, since library calls have no caller',
            d: "the host contract's own address, exactly as for a normal call",
        },
        answer: 'a',
        explanation: "Just like `delegatecall`, the caller context is inherited, so access checks in library-called code see the host's caller.",
    },
    {
        id: 87,
        difficulty: 'hard',
        question: 'The Starknet OS / SHARP (Shared Prover):',
        choices: {
            a: 'batches many transactions into one STARK proof for L1',
            b: 'is a separate blockchain that orders Starknet blocks',
            c: 'replaces the sequencer by proving transactions directly',
            d: 'posts a separate on-chain proof for every transaction',
        },
        answer: 'a',
        explanation: 'One L1 verifier checks the aggregate proof; amortizing verification across a batch is what makes rollup fees low.',
    },
    {
        id: 88,
        difficulty: 'hard',
        question: 'Why can `#[derive(starknet::Store)]` on a large struct be costly?',
        choices: {
            a: 'each field is its own storage cell, so full reads cost O(size)',
            b: 'the derive macro is slow to compile, but runtime cost is flat',
            c: 'it also duplicates the struct into an event on every write',
            d: "it forces the struct's data onto L1 as calldata on each read",
        },
        answer: 'a',
        explanation: 'Fields occupy consecutive storage addresses from the base. Touching one field is fine; reading/writing the whole big struct per call is not. Dynamic data needs `Vec`-style patterns.',
    },
    {
        id: 89,
        difficulty: 'hard',
        question: 'The Starknet state commitment posted to L1 is:',
        choices: {
            a: 'a hash of the contracts-storage trie root and the class trie root',
            b: 'just the hash of the latest Starknet block header, signed',
            c: 'a Merkle root of every event the chain has emitted since genesis',
            d: "the sequencer's ECDSA signature over the latest block's state",
        },
        answer: 'a',
        explanation: 'Global state = (contract trie, class trie); the L1 verifier advances this root only with a valid proof of the transition.',
    },
    {
        id: 90,
        difficulty: 'hard',
        question: 'Overflow behavior differences in Cairo:',
        choices: {
            a: '`u*` addition panics on overflow; raw `felt252` wraps mod P',
            b: 'all integer types, including `felt252`, wrap silently',
            c: 'all integer types saturate at their maximum value instead',
            d: 'only `felt252` panics on overflow; sized types wrap silently',
        },
        answer: 'a',
        explanation: 'Choosing the right type is a safety decision, not just a range decision.',
    },
    {
        id: 91,
        difficulty: 'hard',
        question: 'A "revoked reference" compiler error in Cairo 0 comes from:',
        choices: {
            a: 'an `ap`-based reference lost after ap moves by an unknown amount',
            b: 'using a variable after it has already been moved or dropped',
            c: 'a borrow-checker conflict between two mutable refs, as in Rust',
            d: 'calling a function that is private to a different module',
        },
        answer: 'a',
        explanation: "After a jump or call that changes `ap` unpredictably, references relative to it become unknown (fix: `alloc_locals` / `local`). It is an artifact of Cairo's low-level memory model that Cairo 1 hides.",
    },
    {
        id: 92,
        difficulty: 'hard',
        question: 'Why is native multicall in `__execute__` strictly better than an external Multicall contract for account security?',
        choices: {
            a: 'one tx, one signature, and no extra contract to trust in the path',
            b: 'it compiles faster, so the account class ends up smaller',
            c: 'it avoids paying fees for every call after the first one',
            d: 'it lets each sub-call run without needing an account signature at all',
        },
        answer: 'a',
        explanation: 'All sub-calls share one atomic transaction validated by `__validate__`; the account itself is the batcher, so there is no third-party contract to trust or exploit.',
    },
    {
        id: 93,
        difficulty: 'hard',
        question: 'The `#[flat]` attribute on an event variant is used to:',
        choices: {
            a: "flatten a component's events into the host's event enum",
            b: "compress the event's data fields before they are emitted",
            c: 'mark every field of the event as non-indexed data',
            d: 'forward the event to Ethereum L1 as an L2-to-L1 message',
        },
        answer: 'a',
        explanation: "Without `#[flat]`, the component's events would be nested one level deeper, under the component's variant.",
    },
    {
        id: 94,
        difficulty: 'hard',
        question: 'What does the account nonce protect against, and where is it enforced?',
        choices: {
            a: 'replay; protocol-enforced, and it increments even on revert',
            b: 'nothing; nonces are only advisory hints for wallet UIs',
            c: 'deploy spam; it caps how many contracts an account deploys',
            d: 'replay; but only checked inside `__validate__` by convention',
        },
        answer: 'a',
        explanation: "The protocol requires the transaction nonce to equal the account's current nonce and increments it on inclusion, even when `__execute__` reverts.",
    },
    {
        id: 95,
        difficulty: 'hard',
        question: 'Why can a Cairo program cheaply "compute" a value like a modular inverse or a sorted array?',
        choices: {
            a: 'a hint supplies the answer and the program only verifies it',
            b: 'the sequencer precomputes such values before each execution',
            c: 'Cairo has built-in inverse and sort opcodes that are free',
            d: "it isn't cheap at all; such operations are banned in contracts",
        },
        answer: 'a',
        explanation: 'Verification (e.g. checking x * x^-1 = 1) is cheap; derivation is delegated to the untrusted prover and then checked.',
    },
    {
        id: 96,
        difficulty: 'hard',
        question: 'A contract that does `library_call` to an attacker-chosen class hash is vulnerable because the attacker class can:',
        choices: {
            a: "run arbitrary code on the caller's storage and identity",
            b: "read the caller's storage freely, but never write to it",
            c: "be stopped in time by the account's `__validate__` check",
            d: 'do nothing, since library calls are fully sandboxed',
        },
        answer: 'a',
        explanation: 'It can read/overwrite any storage variable and impersonate the contract in further calls. Never library-call a class hash you do not fully control or trust.',
    },
    {
        id: 97,
        difficulty: 'hard',
        question: 'Why is `get_block_timestamp()` unsafe for high-stakes randomness or tight timing on Starknet today?',
        choices: {
            a: 'the sequencer sets it within loose bounds, so it can be nudged',
            b: 'it always returns zero outside of local test environments',
            c: 'it counts Ethereum L1 block numbers, not seconds, so units are off',
            d: 'it is set by each full node locally, so nodes may disagree',
        },
        answer: 'a',
        explanation: 'It is predictable and influenceable, the same class of problem as `block.timestamp` on Ethereum. Use commit-reveal or an external VRF for randomness.',
    },
    {
        id: 98,
        difficulty: 'hard',
        question: 'A `#[starknet::interface]` dispatcher call that targets a contract not implementing the expected selector will:',
        choices: {
            a: 'revert (entry point not found); there is no silent fallback',
            b: 'succeed and return zeroed data, like a codeless EVM call',
            c: "deploy the missing function from the interface's class",
            d: "fall through to the target's constructor as a fallback",
        },
        answer: 'a',
        explanation: 'Cairo 1 contracts have no implicit fallback; an unknown selector is an error the caller must handle.',
    },
    {
        id: 99,
        difficulty: 'hard',
        question: 'For an upgradeable contract, the safe pattern for post-upgrade initialization is:',
        choices: {
            a: 'a guarded `initialize`-style function called after the upgrade',
            b: "the new class's `#[constructor]`, which runs automatically on upgrade",
            c: "init logic placed inside the account's `__validate__` hook",
            d: 'nothing; storage is migrated to the new layout automatically',
        },
        answer: 'a',
        explanation: 'Access-controlled and protected by an "already initialized" flag, since constructors are a deploy-time concept only.',
    },
    {
        id: 100,
        difficulty: 'hard',
        question: "Storage-layout compatibility across an upgrade is required because a storage variable's address is:",
        choices: {
            a: 'derived from its name, so renames or retypes orphan old data',
            b: 'a sequential slot the compiler keeps stable across versions',
            c: 'stored inside the class hash, so it changes with the class',
            d: 'irrelevant, because Starknet migrates storage during upgrades',
        },
        answer: 'a',
        explanation: 'Renaming, removing, or retyping a variable changes where new code reads/writes. Append new variables; never reorder/rename existing ones during an upgrade.',
    },
];
